import { getContentMediumTypeFromFileNameOrPageUrl } from "../../../backend/utils/content-medium";
import { acceptedFileTypesForWeb, ContentMediumType, JUNGLE_BACKEND_PROD_DOMAIN } from "../../../constants/GlobalTypesAndEnumsAndConstants";
import { getUrlForFileUploadToS3, uploadFileToS3 } from "../../home-page/s3-file-upload";
import { getTextFileContentMediumType } from "../../home-page/anki-text-format";
import {
  assertLocalFileIsReadable,
  createUploadAttemptId,
  FileUploadError,
  FileUploadErrorCode,
  FileUploadInputMethod,
  StorageUploadProgress,
  getPresignedPostBucketName,
  getUploadClaimTokenForObjectKey,
  StableUploadedFileIdentifiers,
  requestFileProcessingTask,
  isRecoverableProcessingResponseError,
} from "../../home-page/file-upload-client";
import { trackErrorWhenLoaded as trackError } from "../../../utils/analytics/track-error-when-loaded";

export type LandingUploadFailureReason =
  | "multiple_files"
  | "empty_file"
  | "unsupported_file_type"
  | "unreadable_file"
  | "presign_failed"
  | "storage_upload_failed"
  | "task_creation_failed";

// Everything the picker's `accept` list allows, by extension — the OS picker's filter can be bypassed
// ("All files"), so the check is repeated here before a byte is uploaded.
const SUPPORTED_UPLOAD_EXTENSIONS = new Set(
  acceptedFileTypesForWeb.split(",").filter((type) => type.startsWith(".")).map((type) => type.slice(1))
);
// Mobile providers sometimes give a readable file a display name without an extension.
// Only a known, explicitly accepted MIME can supply one; an unsupported extension is never overridden.
const EXTENSION_FOR_MIME: Record<string, string> = {
  "application/pdf": "pdf", "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/svg+xml": "svg", "image/heic": "heic", "image/heif": "heif",
  "audio/mpeg": "mp3", "audio/mp3": "mp3", "audio/wav": "wav", "audio/x-wav": "wav",
  "audio/ogg": "ogg", "audio/opus": "opus", "audio/x-m4a": "m4a", "audio/aac": "aac",
  "audio/flac": "flac", "audio/aiff": "aiff", "audio/amr": "amr", "application/vnd.rn-realmedia": "rm",
  "video/quicktime": "mov", "video/mp4": "mp4", "video/x-msvideo": "avi", "text/plain": "txt", "text/markdown": "md",
};

export function normalizeLandingUploadFile(file: File): File {
  const extension = file.name.includes(".") ? file.name.split(".").pop()?.trim().toLowerCase() : undefined;
  const hasSupportedExtension = Boolean(extension) && SUPPORTED_UPLOAD_EXTENSIONS.has(extension);
  if (hasSupportedExtension) {
    // Mobile file providers may include spaces after the dot or at the end.
    // Normalize only an accepted extension; MIME must not override an unknown one.
    const normalizedName = `${file.name.slice(0, file.name.lastIndexOf("."))}.${extension}`;
    return normalizedName === file.name ? file
      : new File([file], normalizedName, { type: file.type, lastModified: file.lastModified });
  }
  const inferredExtension = !file.name.includes(".") && EXTENSION_FOR_MIME[file.type.toLowerCase()];
  if (!inferredExtension) throw new LandingUploadError("unsupported_file_type", UNSUPPORTED_FILE_TYPE_MESSAGE);
  return new File([file], `${file.name || "document"}.${inferredExtension}`, { type: file.type, lastModified: file.lastModified });
}

export const UNSUPPORTED_FILE_TYPE_MESSAGE =
  "That file type isn't supported. Try lecture slides, a PDF, a Word document, an image, or an audio or video file.";

export class LandingUploadError extends Error {
  constructor(
    public readonly reason: LandingUploadFailureReason,
    message: string,
    public readonly cause?: unknown,
    public readonly attemptId?: string,
    public readonly sourceIdentifiers?: StableUploadedFileIdentifiers
  ) {
    super(message);
    this.name = "LandingUploadError";
  }
}

export type LandingUploadResult = {
  // The id of the backend file-processing task the app resumes on arrival (`?fileTaskId=`).
  fileTaskId: string;
  // The file's own name, shown to the visitor in the app.
  fileName: string;
  // How the upload is stored, exactly as the processing task was created with. The app needs these three
  // to claim the anonymous upload for the visitor's account after they sign up (landingHandoffUploadClaim).
  storedFileName: string;
  uploadedFileS3ObjectKey: string;
  contentMediumType: ContentMediumType;
  // The private claim the processing task was created with. The backend generates from an anonymous upload
  // only for a caller that presents it (or the account that claimed the upload), so the app needs it too.
  uploadClaimToken?: string;
};

/**
 * The landing-page upload: presign → upload straight to storage → create the backend task that
 * extracts the file's contents. It returns as soon as the task exists so the visitor can be handed
 * to the app, which listens for the task's result (same as the Framer site did).
 *
 * The task id is generated here and sent with the request: if the response is lost the task still
 * exists under that id, so the handoff can proceed instead of stranding an uploaded file.
 */
export async function uploadFileFromLandingPage(originalFile: File, options: {
  attemptId?: string;
  inputMethod?: FileUploadInputMethod;
  onStage?: (stage: "preparing" | "uploading" | "processing") => void;
  onUploadProgress?: (progress: StorageUploadProgress) => void;
} = {}): Promise<LandingUploadResult> {
  // Keep one identity across validation, storage and processing, including a
  // file provider that becomes unreadable before the first network request.
  const fileTaskId = options.attemptId || createUploadAttemptId();
  options.onStage?.("preparing");
  if (!originalFile || originalFile.size === 0) {
    throw new LandingUploadError("empty_file", "That file is empty. Please choose a different file.");
  }

  const file = normalizeLandingUploadFile(originalFile);
  const fileExtension = file.name.includes(".") ? file.name.split(".").pop()?.toLowerCase() : undefined;
  let formattedFileName = file.name.replace(/\s/g, "+");
  let contentMediumType = getContentMediumTypeFromFileNameOrPageUrl(`document.${fileExtension}`);
  // A file is never a web page; that fallback means the type resolver did not recognise it.
  if (contentMediumType === ContentMediumType.Webpage) {
    throw new LandingUploadError("unsupported_file_type", UNSUPPORTED_FILE_TYPE_MESSAGE);
  }
  // The app converts SVGs to PNG before upload (see uploadFileToS3), so the stored object is a PNG.
  if (fileExtension === "svg") {
    formattedFileName = formattedFileName.replace(/\.svg$/i, ".png");
    contentMediumType = ContentMediumType.Image;
  }

  // Same check the app's uploader runs before presigning: some Android WebViews hand the page a file the
  // browser cannot actually read, and every request carrying it then fails as an opaque network error.
  try {
    await assertLocalFileIsReadable({ file });
  } catch (error) {
    if (error instanceof FileUploadError && error.code === FileUploadErrorCode.FileUnreadable) {
      throw new LandingUploadError(
        "unreadable_file",
        "We couldn't read that file from your device. Try saving it to your device first, then upload it again.",
        error, fileTaskId
      );
    }
    throw error;
  }
  // A .txt file is either an Anki export or notes to generate questions from; only its text says which.
  if (contentMediumType === ContentMediumType.AnkiTxt) contentMediumType = await getTextFileContentMediumType(file);

  let urlForFileUpload: Awaited<ReturnType<typeof getUrlForFileUploadToS3>>;
  try {
    urlForFileUpload = await getUrlForFileUploadToS3(formattedFileName, null, contentMediumType, fileTaskId);
  } catch (error) {
    throw new LandingUploadError(
      "presign_failed",
      "We couldn't start the upload. Please check your connection and try again.", error, fileTaskId
    );
  }

  const sourceIdentifiers = {
    bucketName: getPresignedPostBucketName(urlForFileUpload),
    objectKey: urlForFileUpload.fields.key,
    taskId: fileTaskId,
  };
  try {
    options.onStage?.("uploading");
    await uploadFileToS3(file, urlForFileUpload, null, contentMediumType, fileTaskId, {
      inputMethod: options.inputMethod || "file-picker",
      preferProgressAwareTransport: true,
      onUploadProgress: options.onUploadProgress,
    });
  } catch (error) {
    if (error instanceof FileUploadError && error.code === FileUploadErrorCode.FileUnreadable) {
      throw new LandingUploadError("unreadable_file",
        "We couldn't read that file from your device. Try saving it to your device first, then upload it again.",
        error, fileTaskId, sourceIdentifiers);
    }
    throw new LandingUploadError("storage_upload_failed", "The upload didn't finish. Please try again.", error, fileTaskId, sourceIdentifiers);
  }

  const uploadedFileS3ObjectKey = urlForFileUpload.fields.key;
  // Same construction as the app's upload (readFile): the stored object's URL.
  const storageUploadBaseUrl = urlForFileUpload.url.endsWith("/") ? urlForFileUpload.url : `${urlForFileUpload.url}/`;
  const storageUploadUrl = new URL(uploadedFileS3ObjectKey.split("/").map(encodeURIComponent).join("/"), storageUploadBaseUrl).toString();

  try {
    // The app's own request helper: it sends the client-generated task id (so a lost response still leaves
    // a task the app can wait on), times out, and types its failures. No id token: the visitor is anonymous.
    options.onStage?.("processing");
    await requestFileProcessingTask({
      url: `${JUNGLE_BACKEND_PROD_DOMAIN}/file_or_url/create_task_to_get_file_data`,
      requestedTaskId: fileTaskId,
      body: {
        file_name: formattedFileName,
        page_url: storageUploadUrl,
        uploaded_file_s3_object_key: uploadedFileS3ObjectKey,
        content_medium_type: contentMediumType,
        file_processing_purpose: "new_upload",
      },
      idToken: null,
    });
  } catch (error) {
    // A lost or incomplete success response does not mean the task was not created: the request carried
    // our task id, so the app can still wait on it. It is handed over as usual; the app tells the visitor
    // if that task never existed (the same recovery createAndWaitForFileProcessing does in the app).
    // A backend HTTP refusal remains a failure.
    const isRecoverableResponse = isRecoverableProcessingResponseError(error);
    if (!isRecoverableResponse) {
      throw new LandingUploadError(
        "task_creation_failed",
        "Your file was uploaded but we couldn't start processing it. Please try again.", error, fileTaskId, sourceIdentifiers
      );
    }
    trackError(error, { scope: "landing-page-file-upload", level: "warning",
      tags: { ingestion_attempt_id: fileTaskId, stage: "create_task", outcome: "processing_response_recovery_handoff_attempted" } });
  }

  return {
    fileTaskId,
    fileName: originalFile.name || file.name,
    storedFileName: formattedFileName,
    uploadedFileS3ObjectKey,
    contentMediumType,
    uploadClaimToken: getUploadClaimTokenForObjectKey(uploadedFileS3ObjectKey),
  };
}
